/**
 * Rendu de l'ocean qui ceinture le monde, et de toute l'eau interieure.
 *
 * Trois maillages, trois roles:
 *
 *  - `maillage`, la NAPPE: une seule surface a hauteur de la mer, couvrant le
 *    monde entier plus 180 m de marge sur les quatre cotes. La ou le terrain
 *    remonte au-dessus du niveau de la mer, il masque la nappe tout seul: on n'a
 *    donc pas besoin de la decouper, et les lacs et les rivieres du monde se
 *    remplissent d'eau sans une ligne de code de plus.
 *
 *  - `fond`, le PLATEAU CONTINENTAL: le sol marin hors du monde. Sans lui, l'eau
 *    translucide au-dela du bord de la carte laissait voir le ciel et dessinait
 *    un lisere clair tout autour du monde — exactement le « vide » qu'on ne doit
 *    jamais apercevoir.
 *
 *  - `ecume`, le RIVAGE: un RUBAN continu pose sur la ligne d'eau, extrait du
 *    champ de hauteur par marching squares. Comme il est trouve par balayage, il
 *    suit tout seul les quatre cotes, les criques, les ilots, les lacs et les
 *    rivieres.
 *
 * CE QUI A CHANGE, ET POURQUOI. Le proprietaire a signale une mer laide: des
 * quadrilateres translucides a bords durs semes en pleine mer, une longue arete
 * rectiligne en diagonale, et une etendue turquoise sans relief ni profondeur.
 * Les trois avaient une cause precise:
 *
 *  1. L'ecume etait un SEMIS de carres blancs de deux metres de cote, alignes
 *    sur les axes du monde, poses un par metre de trait de cote et se
 *    chevauchant les uns les autres: chaque carre montrait ses quatre bords et
 *    chaque recouvrement faisait une perle plus claire. C'est maintenant un
 *    RUBAN, dont les deux bords tombent a alpha zero — donc aucune arete — et
 *    dont les segments se raccordent bout a bout au lieu de se superposer.
 *    C'etait bien ces carres-la que le proprietaire voyait autour du lac du
 *    glacier: le balayage les posait sur TOUTE ligne d'eau, lacs compris.
 *    Le ruban gardait cependant un dernier defaut de meme famille, corrige
 *    depuis: voir `plafonnerLargeurs`.
 *
 *  2. L'arete rectiligne etait le bord du monde (x = 0, la ou nage la baigneuse
 *    des apercus). La profondeur d'eau, qui donne sa couleur a la mer, y etait
 *    lue dans le terrain a l'interieur et INVENTEE a l'exterieur (« 4 m, la
 *    moyenne »). Elle est maintenant continue — dehors, on repart de la
 *    profondeur reelle du bord le plus proche avant de plonger vers le large —
 *    puis LISSEE sur la grille, ce qui efface aussi la cassure de pente qu'il
 *    restait a la jointure. Le plateau, lui, part de la hauteur et de la
 *    couleur du terrain au bord.
 *
 *  3. La platitude venait de la nappe: un sommet tous les douze metres, et une
 *    couleur par sommet qui ne faisait varier que la LUMINOSITE d'un unique
 *    turquoise. La nappe a maintenant un sommet tous les quatre metres dans le
 *    monde (la houle a donc du volume en vue a 45 degres), sa couleur est
 *    deduite de la profondeur en trois teintes, sa transparence aussi (on voit
 *    le sable des hauts-fonds, pas le plateau du large), sa normale est
 *    perturbee au pixel par des rides, et un reflet prend la direction, la
 *    couleur et la force du soleil de l'heure.
 *
 * Rien de tout cela n'ajoute de lumiere a la scene — la cle de cache des
 * programmes three.js n'est donc pas touchee — ni de texture, ni d'appel de
 * dessin: on reste a trois.
 */
import * as THREE from 'three'
import { CARTE_X, CARTE_Z, PROFONDEUR_LARGE, SEA_LEVEL } from '../../shared/constants.ts'
import { PORTEE_PLONGEE, mailleSeche, type Carte, type Ile } from '../../shared/sim/carte.ts'
import { clamp, lerp, smoothstep } from '../../shared/sim/alea.ts'
import { RenduChute } from './chute.ts'
import { journal } from '../log/journal.ts'

/** Marge d'ocean au-dela de la carte, en metres. */
const MARGE = 180
const MIN = -MARGE
const MAX_X = CARTE_X + MARGE
const MAX_Z = CARTE_Z + MARGE

/**
 * Jusqu'ou la grille reste FINE autour d'une ile, en metres.
 *
 * Tant qu'il n'y avait qu'une ile, la grille de la mer etait fine partout: le
 * monde entier tenait dans 500 m. Avec sept cents metres de haute mer entre les
 * deux mondes, la maintenir fine coûterait cinq fois plus de sommets pour
 * dessiner une etendue rigoureusement plate — la profondeur ne varie que sur
 * `PORTEE_PLONGEE`, et au-dela le fond est une table. On garde donc le pas fin
 * la ou quelque chose se passe, et un pas large au large.
 */
const BANDE_FINE = 120

/** Pas de la grille en pleine mer, la ou le fond ne varie plus. */
const PAS_LARGE = 20

/**
 * Pas de la nappe DANS le monde, en metres. A quatre metres, la houle la plus
 * courte (29 m) tient sur sept sommets: elle se voit vraiment en vue a 45
 * degres. A douze, comme avant, la mer etait une tole plate.
 */
const PAS_SURFACE = 4

/** Pas du plateau continental a l'interieur du bord du monde, en metres. */
const PAS_FOND = 5

/**
 * Distances, hors du monde, des lignes de grille de la nappe et du plateau.
 * Serrees pres du rivage ou la pente se voit, larges au loin ou tout se fond
 * dans la brume. La derniere vaut MARGE: la nappe s'arrete pile au bord.
 */
const MARCHES_LARGE: readonly number[] = [7, 18, 34, 56, 86, 126, MARGE]


// ------------------------------------------------------------------ couleurs

/** Trois teintes de jour: hauts-fonds, moyen fond, large. */
const JOUR_RIVE = 0x63d9cd
const JOUR_MOYEN = 0x1f9fbe
const JOUR_LARGE = 0x11507f

/**
 * Les memes de nuit. Elles ne sont pas beaucoup plus sombres que celles du
 * jour: l'eclairage de la scene fait deja tomber la mer de deux crans a lui
 * seul, et une teinte de nuit noire par-dessus donnait une mer d'encre. Ce
 * qu'on change ici, c'est surtout la TEINTE — le turquoise part vers l'ardoise.
 */
const NUIT_RIVE = 0x3f8ea0
const NUIT_MOYEN = 0x2a6b90
const NUIT_LARGE = 0x18395f

const TEINTE_NUIT_RIVE = new THREE.Color(NUIT_RIVE)
const TEINTE_NUIT_MOYEN = new THREE.Color(NUIT_MOYEN)
const TEINTE_NUIT_LARGE = new THREE.Color(NUIT_LARGE)

/** Couleur de l'abysse, vers laquelle le plateau se fond en s'eloignant. */
const FOND_ABYSSE = 0x0a2a3e

/** Ecume: blanc a peine cyan, jamais du blanc pur qui ferait tache. */
const COULEUR_ECUME = 0xe4f7f2

// --------------------------------------------------------------------- ecume

/** Pas du balayage qui cherche le trait de cote, en metres. */
const PAS_ECUME = 2
/**
 * Largeur du ruban vers le large, en metres.
 *
 * Volontairement PRESQUE constante. Une largeur deduite de la pente du fond
 * paraissait plus juste, mais le trait de cote de ce monde passe de 25 % a
 * 175 % de pente en quelques metres: le ruban sautait alors du simple au triple
 * d'un segment a l'autre et son bord au large devenait un escalier de plaques
 * pales. Une largeur egale partout, modulee par un sinus lent, se lit comme un
 * ourlet d'ecume et ne fabrique aucune arete.
 */
const LARGEUR_MER = 1.45
/** Part de la largeur du large reportee du cote de la terre. */
const PART_TERRE = 0.55
/** Nombre de passes de lissage de la direction du ruban le long du contour. */
const LISSAGES_ECUME = 3
/**
 * Marge sous la largeur limite d'un virage, et largeur plancher, en metres.
 *
 * Voir `plafonnerLargeurs`: un ruban plus large que le rayon du virage qu'il
 * prend se replie sur lui-meme. La marge garde le pli a distance, le plancher
 * evite qu'un virage tres serre efface completement l'ourlet.
 */
const MARGE_VIRAGE = 0.85
const LARGEUR_MIN = 0.3
/** Variation maximale de la largeur d'un point du contour au suivant, en metres. */
const PENTE_LARGEUR = 0.35
/** Hauteur du ruban au-dessus du niveau de la mer, en metres. */
const HAUTEUR_ECUME = 0.12
/** Opacite de la crete du ruban. */
const ALPHA_ECUME = 0.62
/**
 * Portee, en metres, de la difference centree qui dit de quel cote est le large.
 *
 * Plus large que celle de `Terrain.normale`, qui differencie sur un metre: pres
 * d'un banc de sable de quinze metres, ce gradient-la tourne d'un bout de
 * segment a l'autre et se trompe de sens dans les creux du bruit.
 */
const PORTEE_PENTE = 5
/** Plafond de securite: un monde tres dentele ne doit pas exploser le budget. */
const MAX_SEGMENTS_ECUME = 12000

/**
 * Empeche le ruban d'ecume de se replier sur lui-meme dans les virages serres.
 *
 * Un ruban stocke le long d'une polyligne se retourne des que sa largeur
 * depasse le rayon du virage: le bord exterieur repart alors en arriere, le
 * quadrilatere se croise, et comme le ruban est translucide et sans ecriture de
 * profondeur, le repli se peint DEUX FOIS. C'est ce qui laissait, apres le
 * passage du semis de carres au ruban, quelques echardes pales de deux metres
 * pointees vers le large, notamment sur les pointes de la rive du lac glaciaire.
 *
 * La condition est exacte et locale: le bord decale d'un segment reste dans le
 * sens du segment tant que `d * (dir(b) - dir(a)) . (b - a) > -|b - a| au carre`. On en
 * tire la largeur limite du segment, on garde la plus petite en chaque point,
 * puis on borne la PENTE de la largeur le long du contour pour que le
 * retrecissement soit un fuseau et non une encoche.
 */
function plafonnerLargeurs(
  segments: number[],
  ptX: number[],
  ptZ: number[],
  dirX: Float32Array,
  dirZ: Float32Array,
  largeurs: Float32Array,
): void {
  const nSeg = segments.length / 2
  for (let seg = 0; seg < nSeg; seg++) {
    const a = segments[seg * 2] ?? 0
    const b = segments[seg * 2 + 1] ?? 0
    const ex = (ptX[b] ?? 0) - (ptX[a] ?? 0)
    const ez = (ptZ[b] ?? 0) - (ptZ[a] ?? 0)
    const len2 = ex * ex + ez * ez
    if (len2 < 1e-10) continue
    const k = -(((dirX[b] ?? 0) - (dirX[a] ?? 0)) * ex + ((dirZ[b] ?? 0) - (dirZ[a] ?? 0)) * ez)
    // k <= 0: le bord decale avance dans le meme sens que le segment, donc le
    // quadrilatere ne peut pas se croiser, quelle que soit la largeur.
    if (k <= 1e-9) continue
    const limite = Math.max(LARGEUR_MIN, (len2 / k) * MARGE_VIRAGE)
    if (limite < (largeurs[a] ?? 0)) largeurs[a] = limite
    if (limite < (largeurs[b] ?? 0)) largeurs[b] = limite

    // La ou le contour rebrousse chemin — une pointe de rive large d'une seule
    // maille — les deux normales s'annulent presque, et le lissage rattrape la
    // direction sur celle du voisin: le ruban se couche alors DANS l'axe du
    // contour et sort en lame de deux metres pointee vers le large. On la
    // ramene a l'ourlet minimal, ou elle ne se voit plus.
    const lg = Math.sqrt(len2)
    for (let bout = 0; bout < 2; bout++) {
      const p = bout === 0 ? a : b
      const penche = Math.abs(((dirX[p] ?? 0) * ex + (dirZ[p] ?? 0) * ez) / lg)
      const garde = LARGEUR_MIN + ((largeurs[p] ?? 0) - LARGEUR_MIN) * (1 - smoothstep(0.55, 0.9, penche))
      if (garde < (largeurs[p] ?? 0)) largeurs[p] = garde
    }
  }

  // Deux passes suffisent a etaler le retrecissement sur quelques metres.
  for (let passe = 0; passe < 2; passe++) {
    for (let seg = 0; seg < nSeg; seg++) {
      const a = segments[seg * 2] ?? 0
      const b = segments[seg * 2 + 1] ?? 0
      const la = largeurs[a] ?? 0
      const lb = largeurs[b] ?? 0
      if (lb > la + PENTE_LARGEUR) largeurs[b] = la + PENTE_LARGEUR
      else if (la > lb + PENTE_LARGEUR) largeurs[a] = lb + PENTE_LARGEUR
    }
  }
}

export class RenduEau {
  /** Temps partage par les nuanceurs de la houle et du ressac. */
  private uTemps = { value: 0 }
  /** Direction de la source lumineuse, en monde: elle pilote le reflet. */
  private uSoleil = { value: new THREE.Vector3(0.6, 0.7, 0.4) }
  /** Couleur x force du reflet: ambre au couchant, a peine argente la nuit. */
  private uEclat = { value: new THREE.Color(0, 0, 0) }
  private uRive = { value: new THREE.Color(JOUR_RIVE) }
  private uMoyen = { value: new THREE.Color(JOUR_MOYEN) }
  private uLarge = { value: new THREE.Color(JOUR_LARGE) }

  readonly maillage: THREE.Mesh

  private scene: THREE.Scene
  private geo: THREE.BufferGeometry
  private mat: THREE.MeshLambertMaterial
  private fond: THREE.Mesh | null = null
  private matFond: THREE.MeshLambertMaterial | null = null
  private ecume: THREE.Mesh | null = null
  private matEcume: THREE.MeshLambertMaterial | null = null
  private nuit = 0
  private soleil: THREE.DirectionalLight | null = null
  private cibleSoleil: THREE.Object3D | null = null
  /**
   * La chute d'eau. Elle vit ici parce qu'elle est de l'eau et rien d'autre:
   * elle recoit le meme temps et la meme nuit que la nappe, et l'appelant n'a
   * pas une ligne a changer pour qu'elle existe.
   */
  private chutes: RenduChute[]

  constructor(scene: THREE.Scene, carte: Carte) {
    this.scene = scene
    const t0 = performance.now()

    const { geo, sommets } = this.construireSurface(carte)
    this.geo = geo

    this.mat = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: true,
      // L'opacite reelle est calculee au pixel a partir de la profondeur; celle
      // du materiau ne sert qu'a faire classer la nappe dans les transparents.
      opacity: 1,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.installerNuanceurSurface(this.mat)

    this.maillage = new THREE.Mesh(this.geo, this.mat)
    this.maillage.name = 'eau'
    this.maillage.position.set(0, SEA_LEVEL, 0)
    this.maillage.renderOrder = 2
    this.maillage.receiveShadow = false
    this.maillage.castShadow = false
    scene.add(this.maillage)

    const trisFond = this.construireFond(carte)
    const segments = this.construireEcume(carte)
    /*
     * UNE CHUTE PAR ILE QUI EN A UNE. Il n'y en avait qu'une, sur l'ile 1;
     * l'ile 2 a la sienne depuis que sa Cascade de l'Etoile existe pour de bon.
     * Trois appels de dessin de plus, et ZERO recompilation de shader: les
     * clefs de cache de programme sont des chaines constantes, donc la seconde
     * chute reutilise les programmes de la premiere.
     */
    this.chutes = carte.iles.filter((ile) => ile.chute).map((ile) => new RenduChute(scene, ile))
    journal.info(
      'eau',
      `nappe ${sommets} sommets · plateau ${trisFond} triangles · ${segments} segments d'ecume`,
      { ms: Math.round(performance.now() - t0) },
    )
  }

  // -------------------------------------------------------------- profondeur

  /**
   * Profondeur d'eau en un point, en metres, CONTINUE de part et d'autre du
   * bord du monde.
   *
   * C'est la brique qui supprime la longue arete rectiligne: hors du monde on
   * ne part plus d'une valeur inventee mais de la profondeur reelle du bord le
   * plus proche, et on ne plonge vers le large qu'ensuite. Le bord du monde est
   * partout sous l'eau (au plus haut -1,80 m), la formule n'a donc pas de cas
   * « terre » a traiter dehors, et les deux branches se rejoignent exactement a
   * dehors = 0.
   */
  private profondeur(carte: Carte, x: number, z: number): number {
    return Math.max(0, SEA_LEVEL - carte.hauteur(x, z))
  }

  /**
   * Lignes de grille d'un AXE: fines pres des iles, larges au large, et les
   * marches habituelles au-dela de la carte.
   *
   * Une grille en produit tensoriel (les memes valeurs sur tout l'axe) n'a
   * jamais de sommet en T, donc jamais de fissure entre deux mailles de tailles
   * differentes — c'est ce qui permet de degrader le pas sans y penser. Les
   * bornes des iles sont forcement dans la liste: le plateau saute les mailles
   * qui sont A L'INTERIEUR d'une ile, et il faut donc qu'une maille ne puisse
   * pas etre a cheval sur un rivage.
   */
  private lignesAxe(pas: number, etendue: number, bandes: readonly [number, number][]): number[] {
    const vus = new Set<number>()
    const u: number[] = []
    const poser = (v: number): void => {
      const r = Math.round(v * 100) / 100
      if (vus.has(r)) return
      vus.add(r)
      u.push(r)
    }
    for (let k = MARCHES_LARGE.length - 1; k >= 0; k--) poser(-(MARCHES_LARGE[k] ?? 0))
    for (let v = 0; v <= etendue; v += PAS_LARGE) poser(v)
    poser(etendue)
    for (const [a, b] of bandes) {
      /*
       * Le debut est RAMENE SUR LE RESEAU du pas, et ce n'est pas un detail:
       * deux bandes qui commencent a 0 et a 30 avec un pas de 4 ne partagent
       * aucune valeur, donc leurs lignes s'intercalent au lieu de se
       * superposer. Mesure: la nappe passait de 38 000 a 68 500 sommets pour
       * couvrir exactement la meme mer.
       */
      const debut = Math.floor(Math.max(0, a - BANDE_FINE) / pas) * pas
      const fin = Math.min(etendue, b + BANDE_FINE)
      for (let v = debut; v <= fin; v += pas) poser(v)
      poser(a)
      poser(b)
      poser(fin)
    }
    for (const d of MARCHES_LARGE) poser(etendue + d)
    u.sort((p, q) => p - q)
    return u
  }

  /**
   * Les bandes fines sur x et sur z: une par ile.
   *
   * ON A ESSAYE DE FAIRE MIEUX, ET LA MESURE A DIT NON. L'idee etait de ne
   * raffiner qu'aux RIVAGES plutot que sur l'emprise entiere — la profondeur ne
   * varie que pres d'une cote, apres tout. Mais une cote fait le TOUR d'une
   * ile: sur une grille en produit tensoriel, chaque colonne et chaque rangee
   * rencontre donc de l'eau quelque part, et les bandes « aux rivages »
   * ressortent exactement egales a l'emprise. Le calcul savant rendait le
   * meme resultat que les deux lignes qui suivent.
   *
   * Ce qui paie vraiment est ailleurs: sauter les mailles SECHES a la pose des
   * indices (`construireSurface`).
   */
  private bandes(carte: Carte): { x: [number, number][]; z: [number, number][] } {
    const x: [number, number][] = []
    const z: [number, number][] = []
    for (const ile of carte.iles) {
      x.push([ile.x0, ile.x0 + ile.cote])
      z.push([ile.z0, ile.z0 + ile.cote])
    }
    return { x, z }
  }

  // ------------------------------------------------------------------- nappe

  private construireSurface(carte: Carte): { geo: THREE.BufferGeometry; sommets: number } {
    const bandes = this.bandes(carte)
    const u = this.lignesAxe(PAS_SURFACE, CARTE_X, bandes.x)
    const v = this.lignesAxe(PAS_SURFACE, CARTE_Z, bandes.z)
    const n = u.length
    const m = v.length
    const nb = n * m
    const positions = new Float32Array(nb * 3)
    const normales = new Float32Array(nb * 3)
    const profondeurs = new Float32Array(nb)

    for (let j = 0; j < m; j++) {
      const z = v[j] ?? 0
      for (let i = 0; i < n; i++) {
        const x = u[i] ?? 0
        const k = j * n + i
        positions[k * 3] = x
        positions[k * 3 + 1] = 0
        positions[k * 3 + 2] = z
        normales[k * 3] = 0
        normales[k * 3 + 1] = 1
        normales[k * 3 + 2] = 0
        profondeurs[k] = this.profondeur(carte, x, z)
      }
    }

    // La profondeur est LISSEE sur la grille avant de servir.
    //
    // Sans cela il restait, en travers du large, un mince trait clair: le bord
    // du monde. La profondeur y est pourtant continue, mais sa PENTE ne l'est
    // pas — dedans elle suit le fond marin, dehors elle repart de zero avant de
    // plonger — et l'oeil lit tres bien la cassure d'un degrade. Trois passes
    // de moyenne l'etalent sur une maille de chaque cote et il n'en reste rien.
    // Au passage, le grain du fond marin cesse de marbrer la couleur de l'eau.
    const tampon = new Float32Array(nb)
    for (let passe = 0; passe < 3; passe++) {
      for (let j = 0; j < m; j++) {
        for (let i = 0; i < n; i++) {
          const k = j * n + i
          const g = profondeurs[k - (i > 0 ? 1 : 0)] ?? 0
          const dr = profondeurs[k + (i < n - 1 ? 1 : 0)] ?? 0
          const hau = profondeurs[k - (j > 0 ? n : 0)] ?? 0
          const bas = profondeurs[k + (j < m - 1 ? n : 0)] ?? 0
          tampon[k] = 0.5 * (profondeurs[k] ?? 0) + 0.125 * (g + dr + hau + bas)
        }
      }
      profondeurs.set(tampon)
    }

    /*
     * Uint32: la nappe couvre 1900 m sur 700 et passe de loin les 65 536
     * sommets qu'un indice de 16 bits sait designer.
     *
     * ON SAUTE LES MAILLES ENTIEREMENT SECHES. Le plateau continental le
     * faisait deja (`dansUneIle`, plus bas), la nappe non: elle posait donc de
     * l'eau sous chaque metre carre de terre du monde, invisible a jamais. Le
     * test n'est pas « suis-je dans une ile » mais « cette maille est-elle
     * SECHE », et il regarde le minimum exact du terrain: un lac au milieu de
     * l'ile 2 garde son eau sans qu'une seule ligne parle de lac.
     */
    const idx = new Uint32Array((n - 1) * (m - 1) * 6)
    let q = 0
    for (let j = 0; j < m - 1; j++) {
      const z0 = v[j] ?? 0
      const z1 = v[j + 1] ?? 0
      for (let i = 0; i < n - 1; i++) {
        const x0 = u[i] ?? 0
        const x1 = u[i + 1] ?? 0
        if (mailleSeche(carte, x0, x1, z0, z1)) continue
        const a = j * n + i
        idx[q] = a
        idx[q + 1] = a + n
        idx[q + 2] = a + 1
        idx[q + 3] = a + 1
        idx[q + 4] = a + n
        idx[q + 5] = a + n + 1
        q += 6
      }
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('normal', new THREE.BufferAttribute(normales, 3))
    geo.setAttribute('aProfondeur', new THREE.BufferAttribute(profondeurs, 1))
    // On ne garde que les indices reellement poses.
    geo.setIndex(new THREE.BufferAttribute(idx.subarray(0, q), 1))
    // Sphere posee a la main: la houle sort du volume calcule a plat.
    geo.boundingSphere = new THREE.Sphere(
      new THREE.Vector3((MIN + MAX_X) / 2, 0, (MIN + MAX_Z) / 2),
      0.5 * Math.hypot(MAX_X - MIN, MAX_Z - MIN) + 1,
    )
    return { geo, sommets: nb }
  }

  // ---------------------------------------------------- plateau continental

  /**
   * Hauteur de reference du plateau AU RIVAGE: le MAXIMUM du terrain le long du
   * bord, sur la portee d'une maille de chaque cote.
   *
   * Prendre le maximum et non la valeur au point n'est pas de la coquetterie.
   * Le plateau raccorde le terrain par une corde droite tous les cinq metres,
   * alors que le bord d'une ile, lui, ondule au metre. Si la corde passait SOUS
   * le terrain, il resterait entre les deux une fente verticale par laquelle on
   * verrait le ciel — le « vide » qu'on ne doit jamais apercevoir. En passant
   * au-dessus, le pire cas devient un ressaut de sol immerge, invisible.
   */
  private hauteurBord(ile: Ile, bx: number, bz: number): number {
    let h = ile.hauteur(bx, bz)
    const x1 = ile.x0 + ile.cote
    const z1 = ile.z0 + ile.cote
    const surX = bx <= ile.x0 || bx >= x1
    const surZ = bz <= ile.z0 || bz >= z1
    for (let d = -PAS_FOND; d <= PAS_FOND; d++) {
      if (surX) h = Math.max(h, ile.hauteur(bx, clamp(bz + d, ile.z0, z1)))
      if (surZ) h = Math.max(h, ile.hauteur(clamp(bx + d, ile.x0, x1), bz))
    }
    return h
  }

  /**
   * Le fond marin hors des iles: il ferme l'horizon, et depuis le deuxieme
   * monde il porte aussi les sept cents metres de haute mer qu'on traverse.
   *
   * C'est un CADRE, pas un disque: les mailles entierement A L'INTERIEUR d'une
   * ile sont sautees, sinon ce plan opaque masquerait le vrai fond marin dans
   * les golfes profonds — et c'est aussi lui qui evite de quadriller en terrain
   * une mer que personne ne foulera jamais (voir client/render/terrain.ts).
   *
   * Sa premiere ligne suit le rivage en hauteur ET en couleur (`couleurSol`, la
   * fonction meme dont se sert le terrain): c'est ce raccord qui fait
   * disparaitre le lisere rectiligne qu'on voyait a travers l'eau. Ensuite il
   * plonge vers l'abysse, ce qui prolonge la pente du littoral au lieu de la
   * couper net.
   */
  private construireFond(carte: Carte): number {
    const bandes = this.bandes(carte)
    const u = this.lignesAxe(PAS_FOND, CARTE_X, bandes.x)
    const v = this.lignesAxe(PAS_FOND, CARTE_Z, bandes.z)
    const n = u.length
    const m = v.length
    const positions: number[] = []
    const couleurs: number[] = []
    const loin = new THREE.Color(FOND_ABYSSE)
    const teinte = new THREE.Color()

    // Hauteurs et couleurs calculees une fois par NOEUD: chaque noeud sert a six
    // sommets, et `couleurSol` empile assez de bruits pour que le recalculer six
    // fois se voie au demarrage.
    const hauteurs = new Float32Array(n * m)
    const rgb = new Float32Array(n * m * 3)
    for (let j = 0; j < m; j++) {
      const z = v[j] ?? 0
      for (let i = 0; i < n; i++) {
        const x = u[i] ?? 0
        const k = j * n + i
        const p = carte.bordProche(x, z)
        const dehors = carte.ileDe(x, z) ? 0 : p.d
        // Les noeuds franchement a l'interieur n'appartiennent qu'a des mailles
        // sautees: inutile de balayer le rivage pour eux.
        const auBord =
          p.bx <= p.ile.x0 + PAS_FOND ||
          p.bx >= p.ile.x0 + p.ile.cote - PAS_FOND ||
          p.bz <= p.ile.z0 + PAS_FOND ||
          p.bz >= p.ile.z0 + p.ile.cote - PAS_FOND
        const hBord = auBord ? this.hauteurBord(p.ile, p.bx, p.bz) : p.ile.hauteur(p.bx, p.bz)
        hauteurs[k] = lerp(hBord, SEA_LEVEL - PROFONDEUR_LARGE, smoothstep(0, PORTEE_PLONGEE + 15, dehors))
        teinte.setHex(p.ile.couleurSol(p.bx, p.bz)).lerp(loin, smoothstep(0, 240, dehors))
        rgb[k * 3] = teinte.r
        rgb[k * 3 + 1] = teinte.g
        rgb[k * 3 + 2] = teinte.b
      }
    }

    const poser = (i: number, j: number): void => {
      const k = j * n + i
      positions.push(u[i] ?? 0, hauteurs[k] ?? 0, v[j] ?? 0)
      couleurs.push(rgb[k * 3] ?? 0, rgb[k * 3 + 1] ?? 0, rgb[k * 3 + 2] ?? 0)
    }

    /** Vrai si la maille est entierement dans UNE ile: le terrain y fait le fond. */
    const dansUneIle = (x0: number, x1: number, z0: number, z1: number): boolean => {
      for (const ile of carte.iles) {
        if (x0 >= ile.x0 && x1 <= ile.x0 + ile.cote && z0 >= ile.z0 && z1 <= ile.z0 + ile.cote) return true
      }
      return false
    }

    for (let j = 0; j < m - 1; j++) {
      for (let i = 0; i < n - 1; i++) {
        if (dansUneIle(u[i] ?? 0, u[i + 1] ?? 0, v[j] ?? 0, v[j + 1] ?? 0)) continue
        poser(i, j)
        poser(i, j + 1)
        poser(i + 1, j)
        poser(i + 1, j)
        poser(i, j + 1)
        poser(i + 1, j + 1)
      }
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3))
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(couleurs), 3))
    geo.computeVertexNormals()
    geo.computeBoundingSphere()

    // Le plateau est eclaire comme le terrain et ne recoit aucune teinte de
    // nuit: c'est ce qui garde les deux sols de la meme couleur a toute heure,
    // donc le raccord invisible au rivage.
    this.matFond = new THREE.MeshLambertMaterial({ vertexColors: true })
    this.fond = new THREE.Mesh(geo, this.matFond)
    this.fond.name = 'plateau-continental'
    this.fond.receiveShadow = false
    this.fond.castShadow = false
    this.fond.matrixAutoUpdate = false
    this.fond.updateMatrix()
    this.scene.add(this.fond)
    return positions.length / 9
  }

  // ------------------------------------------------------------------ ecume

  /**
   * Ecume: un RUBAN continu pose sur la ligne d'eau.
   *
   * Le trait de cote est extrait par marching squares sur la grille des
   * hauteurs. Les points de contour sont SOUDES par arete de maille: deux
   * mailles voisines qui traversent la meme arete se partagent le meme point,
   * et le contour forme donc de vraies polylignes et non un tas de segments.
   *
   * Cette soudure est ce qui rend le ruban propre. La direction du large et la
   * largeur sont calculees UNE FOIS PAR POINT, en moyennant les segments qui s'y
   * rejoignent: deux quads voisins partagent alors exactement le meme bord, si
   * bien qu'il n'y a ni fente ni chevauchement. Les versions precedentes
   * prenaient la direction dans le gradient du terrain, point par point: pres
   * d'un banc de sable ce gradient tourne en quelques metres, les quads
   * s'ouvraient en eventail, et le rivage se hachait en dents de scie pales
   * pointees vers le large — exactement ce qu'on cherchait a supprimer.
   *
   * Chaque bout de segment porte quatre rangs de sommets, du large vers la
   * terre, dont le premier et le dernier sont a alpha zero: le ruban n'a donc
   * AUCUNE arete, il s'eteint des deux cotes.
   *
   * `aMer` et `aPhase` servent au ressac: le nuanceur fait avancer et reculer
   * le ruban le long de la normale a la cote.
   */
  private construireEcume(carte: Carte): number {
    const ptX: number[] = []
    const ptZ: number[] = []
    const segments: number[] = []
    // Un balayage PAR ILE. Le contour se cherche la ou il y a une cote, et il
    // n'y en a pas au milieu de l'ocean: quadriller les 1400 m de la carte
    // reviendrait a chercher une ligne d'eau dans sept cents metres d'eau.
    for (const ile of carte.iles) this.scannerCote(ile, ptX, ptZ, segments)
    return this.rubanEcume(carte, ptX, ptZ, segments)
  }

  /**
   * Le trait de cote d'UNE ile, par marching squares, ajoute aux tableaux
   * communs. Les points sont soudes par arete de maille, a l'interieur de
   * l'ile: deux iles ne partagent aucune arete, donc rien a coordonner.
   */
  private scannerCote(ile: Ile, ptX: number[], ptZ: number[], segments: number[]): void {
    const cases = Math.ceil(ile.cote / PAS_ECUME)
    const cote = cases + 1
    const fin = ile.cote
    const h = new Float32Array(cote * cote)
    for (let j = 0; j < cote; j++) {
      const z = ile.z0 + Math.min(fin, j * PAS_ECUME)
      for (let i = 0; i < cote; i++) {
        h[j * cote + i] = ile.hauteur(ile.x0 + Math.min(fin, i * PAS_ECUME), z) - SEA_LEVEL
      }
    }
    const lire = (i: number, j: number): number => h[j * cote + i] ?? 0

    // Un point de contour appartient a une arete de la grille et a une seule:
    // son numero d'arete sert donc de clef de soudure, sans table de hachage.
    // Aretes paires: horizontales (i,j)-(i+1,j). Impaires: verticales.
    const clefs = new Int32Array(2 * cote * cote).fill(-1)
    const souder = (clef: number, x: number, z: number): number => {
      const vu = clefs[clef] ?? -1
      if (vu >= 0) return vu
      const n = ptX.length
      ptX.push(x)
      ptZ.push(z)
      clefs[clef] = n
      return n
    }

    const bouts: number[] = []
    for (let j = 0; j < cases && segments.length < MAX_SEGMENTS_ECUME * 2; j++) {
      for (let i = 0; i < cases; i++) {
        const x0 = ile.x0 + i * PAS_ECUME
        const z0 = ile.z0 + j * PAS_ECUME
        const a = lire(i, j)
        const b = lire(i + 1, j)
        const c = lire(i, j + 1)
        const d = lire(i + 1, j + 1)
        bouts.length = 0
        // Aretes de la maille dans l'ordre bas, droite, haut, gauche: il suffit
        // a apparier les deux bouts d'une traversee.
        if (a < 0 !== b < 0) {
          bouts.push(souder(2 * (j * cote + i), x0 + PAS_ECUME * (a / (a - b)), z0))
        }
        if (b < 0 !== d < 0) {
          bouts.push(souder(2 * (j * cote + i + 1) + 1, x0 + PAS_ECUME, z0 + PAS_ECUME * (b / (b - d))))
        }
        if (c < 0 !== d < 0) {
          bouts.push(souder(2 * ((j + 1) * cote + i), x0 + PAS_ECUME * (c / (c - d)), z0 + PAS_ECUME))
        }
        if (a < 0 !== c < 0) {
          bouts.push(souder(2 * (j * cote + i) + 1, x0, z0 + PAS_ECUME * (a / (a - c))))
        }
        // Deux bouts: un segment. Quatre (une selle): on apparie dans l'ordre,
        // la topologie exacte d'une selle ne se voit pas sur de l'ecume.
        for (let p = 0; p + 1 < bouts.length; p += 2) {
          segments.push(bouts[p] ?? 0, bouts[p + 1] ?? 0)
        }
      }
    }
  }

  /** Le ruban lui-meme, a partir des contours de toutes les iles. */
  private rubanEcume(carte: Carte, ptX: number[], ptZ: number[], segments: number[]): number {
    // --- direction du large et pente, moyennees par POINT.
    const nbPts = ptX.length
    const dirX = new Float32Array(nbPts)
    const dirZ = new Float32Array(nbPts)
    const nSeg = segments.length / 2
    for (let seg = 0; seg < nSeg; seg++) {
      const p0 = segments[seg * 2] ?? 0
      const p1 = segments[seg * 2 + 1] ?? 0
      const ax = ptX[p0] ?? 0
      const az = ptZ[p0] ?? 0
      const bx = ptX[p1] ?? 0
      const bz = ptZ[p1] ?? 0
      const tx = bx - ax
      const tz = bz - az
      const lt = Math.hypot(tx, tz)
      if (lt < 1e-6) continue
      // Le gradient du terrain descend la pente, donc il montre le large: il ne
      // sert plus qu'a ORIENTER la normale du contour, pas a la definir.
      const d = PORTEE_PENTE
      const mx = (ax + bx) / 2
      const mz = (az + bz) / 2
      const gx = carte.hauteur(mx - d, mz) - carte.hauteur(mx + d, mz)
      const gz = carte.hauteur(mx, mz - d) - carte.hauteur(mx, mz + d)
      let nx = tz / lt
      let nz = -tx / lt
      if (nx * gx + nz * gz < 0) {
        nx = -nx
        nz = -nz
      }
      dirX[p0] = (dirX[p0] ?? 0) + nx * lt
      dirZ[p0] = (dirZ[p0] ?? 0) + nz * lt
      dirX[p1] = (dirX[p1] ?? 0) + nx * lt
      dirZ[p1] = (dirZ[p1] ?? 0) + nz * lt
    }

    const normaliser = (p: number): void => {
      const nx = dirX[p] ?? 0
      const nz = dirZ[p] ?? 0
      const l = Math.hypot(nx, nz)
      if (l < 1e-4) {
        // Point isole, ou virage a 180 degres ou les deux normales s'annulent:
        // il n'y a plus de direction a tirer du contour, on couche le ruban
        // vers l'est.
        dirX[p] = 1
        dirZ[p] = 0
      } else {
        dirX[p] = nx / l
        dirZ[p] = nz / l
      }
    }
    for (let p = 0; p < nbPts; p++) normaliser(p)

    // Lissage de la direction LE LONG DU CONTOUR. Deux points voisins qui
    // penchent differemment ouvrent leurs quads en eventail, et l'eventail se
    // lit comme un coin pale en travers de la mer. Quelques passes de moyenne
    // suffisent a coucher tout le ruban dans le meme sens.
    const accX = new Float32Array(nbPts)
    const accZ = new Float32Array(nbPts)
    for (let passe = 0; passe < LISSAGES_ECUME; passe++) {
      accX.fill(0)
      accZ.fill(0)
      for (let seg = 0; seg < nSeg; seg++) {
        const p0 = segments[seg * 2] ?? 0
        const p1 = segments[seg * 2 + 1] ?? 0
        accX[p0] = (accX[p0] ?? 0) + (dirX[p1] ?? 0)
        accZ[p0] = (accZ[p0] ?? 0) + (dirZ[p1] ?? 0)
        accX[p1] = (accX[p1] ?? 0) + (dirX[p0] ?? 0)
        accZ[p1] = (accZ[p1] ?? 0) + (dirZ[p0] ?? 0)
      }
      for (let p = 0; p < nbPts; p++) {
        dirX[p] = (dirX[p] ?? 0) + (accX[p] ?? 0) * 0.5
        dirZ[p] = (dirZ[p] ?? 0) + (accZ[p] ?? 0) * 0.5
        normaliser(p)
      }
    }

    // La largeur ondule par des sinus LENTS: une variation continue en espace
    // garde le ruban mousseux sans jamais fabriquer de dent.
    const largeurs = new Float32Array(nbPts)
    for (let p = 0; p < nbPts; p++) {
      const x = ptX[p] ?? 0
      const z = ptZ[p] ?? 0
      const bruit = Math.sin(x * 0.31 + z * 0.24) * 0.6 + Math.sin(x * 0.11 - z * 0.17) * 0.4
      largeurs[p] = LARGEUR_MER * (1 + 0.22 * bruit)
    }
    plafonnerLargeurs(segments, ptX, ptZ, dirX, dirZ, largeurs)

    // --- ruban.
    const positions = new Float32Array(nSeg * 8 * 3)
    const couleurs = new Float32Array(nSeg * 8 * 4)
    const mers = new Float32Array(nSeg * 8 * 2)
    const phases = new Float32Array(nSeg * 8)
    const idx = new Uint32Array(nSeg * 18)
    const normales = new Float32Array(nSeg * 8 * 3)
    const base = new THREE.Color(COULEUR_ECUME)
    // Decalages depuis la ligne d'eau (positif = vers le large) et opacites.
    const rangs: readonly [number, number][] = [
      [1, 0],
      [0.3, 1],
      [-0.35 * PART_TERRE, 0.85],
      [-PART_TERRE, 0],
    ]

    let s = 0
    for (let seg = 0; seg < nSeg; seg++) {
      const depart = seg * 8
      for (let bout = 0; bout < 2; bout++) {
        const p = segments[seg * 2 + bout] ?? 0
        const x = ptX[p] ?? 0
        const z = ptZ[p] ?? 0
        const mx = dirX[p] ?? 1
        const mz = dirZ[p] ?? 0
        const large = largeurs[p] ?? 1
        const alpha = ALPHA_ECUME * (0.78 + 0.22 * Math.sin(x * 0.19 - z * 0.13))
        const phase = x * 0.11 + z * 0.085
        for (let r = 0; r < 4; r++) {
          const rang = rangs[r] ?? [0, 0]
          const decalage = (rang[0] ?? 0) * large
          const v = depart + bout * 4 + r
          positions[v * 3] = x + mx * decalage
          positions[v * 3 + 1] = HAUTEUR_ECUME
          positions[v * 3 + 2] = z + mz * decalage
          normales[v * 3] = 0
          normales[v * 3 + 1] = 1
          normales[v * 3 + 2] = 0
          couleurs[v * 4] = base.r
          couleurs[v * 4 + 1] = base.g
          couleurs[v * 4 + 2] = base.b
          couleurs[v * 4 + 3] = alpha * (rang[1] ?? 0)
          mers[v * 2] = mx
          mers[v * 2 + 1] = mz
          phases[v] = phase
        }
      }
      // Trois bandes de quads entre les quatre rangs.
      for (let r = 0; r < 3; r++) {
        const a0 = depart + r
        const b0 = depart + r + 1
        const a1 = depart + 4 + r
        const b1 = depart + 5 + r
        idx[s] = a0
        idx[s + 1] = a1
        idx[s + 2] = b0
        idx[s + 3] = b0
        idx[s + 4] = a1
        idx[s + 5] = b1
        s += 6
      }
    }


    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('normal', new THREE.BufferAttribute(normales, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(couleurs, 4))
    geo.setAttribute('aMer', new THREE.BufferAttribute(mers, 2))
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))
    geo.setIndex(new THREE.BufferAttribute(idx, 1))
    geo.computeBoundingSphere()
    // Le ressac deplace le ruban de trois quarts de metre: la sphere calculee a
    // plat ne le contiendrait pas.
    if (geo.boundingSphere) geo.boundingSphere.radius += 2

    // ECLAIRE, et non `MeshBasicMaterial`. Un blanc qui ne repond a aucune
    // lumiere reste un blanc a 20 h 30: le ruban lavait alors toute la cote
    // d'un gris pale, plus clair que le sable qu'il bordait. Eclaire par la
    // meme source que la mer et le sable, il devient ambre au couchant et
    // s'eteint la nuit tout seul.
    this.matEcume = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.installerNuanceurEcume(this.matEcume)

    this.ecume = new THREE.Mesh(geo, this.matEcume)
    this.ecume.name = 'ecume'
    this.ecume.renderOrder = 3
    this.maillage.add(this.ecume)
    return nSeg
  }

  // -------------------------------------------------------------- nuanceurs

  /**
   * Houle, profondeur, rides et reflet, greffes dans le nuanceur de la nappe.
   *
   * `onBeforeCompile` permet de garder MeshLambertMaterial — donc l'eclairage,
   * les ombres recues et la brume du jeu — tout en calculant le mouvement sur
   * le GPU.
   *
   * Deux echelles, deliberement: la houle DEPLACE les sommets (elle donne du
   * volume et de la silhouette en vue a 45 degres), les rides ne perturbent que
   * la NORMALE au pixel (elles donnent le frisson sans un triangle de plus). La
   * normale perturbee passe par l'eclairage Lambert habituel: la mer repond
   * donc a la couleur et a la hauteur du soleil de l'heure sans qu'on ajoute la
   * moindre lumiere a la scene.
   */
  private installerNuanceurSurface(materiau: THREE.Material): void {
    materiau.customProgramCacheKey = (): string => 'eau-nappe'
    materiau.onBeforeCompile = (shader) => {
      shader.uniforms['uTemps'] = this.uTemps
      shader.uniforms['uSoleil'] = this.uSoleil
      shader.uniforms['uEclat'] = this.uEclat
      shader.uniforms['uRive'] = this.uRive
      shader.uniforms['uMoyen'] = this.uMoyen
      shader.uniforms['uLarge'] = this.uLarge

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTemps;
           attribute float aProfondeur;
           varying float vProf;
           varying vec3 vMonde;
           float houle(vec2 p, float t) {
             return 0.19 * sin(dot(p, vec2(0.090, 0.050)) + t * 0.85)
                  + 0.13 * sin(dot(p, vec2(-0.050, 0.130)) + t * 1.15)
                  + 0.07 * sin(dot(p, vec2(0.170, 0.130)) - t * 1.55);
           }`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           // La houle s'eteint sur les hauts-fonds: sinon la nappe battait le
           // sable, decouvrait la plage et faisait clignoter le trait de cote.
           float calme = smoothstep(0.25, 3.0, aProfondeur);
           float onde = houle(position.xz, uTemps) * calme;
           transformed.y += onde;
           vProf = aProfondeur;
           vMonde = vec3(position.x, onde, position.z);`,
        )

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTemps;
           uniform vec3 uSoleil;
           uniform vec3 uEclat;
           uniform vec3 uRive;
           uniform vec3 uMoyen;
           uniform vec3 uLarge;
           varying float vProf;
           varying vec3 vMonde;
           /** Pente de la surface, houle et rides reunies, en monde. */
           vec2 penteEau(vec2 p, float t) {
             vec2 g = vec2(0.0);
             g += vec2(0.090, 0.050) * (0.19 * cos(dot(p, vec2(0.090, 0.050)) + t * 0.85));
             g += vec2(-0.050, 0.130) * (0.13 * cos(dot(p, vec2(-0.050, 0.130)) + t * 1.15));
             g += vec2(0.170, 0.130) * (0.07 * cos(dot(p, vec2(0.170, 0.130)) - t * 1.55));
             // Cinq rides, et des directions volontairement sans rapport
             // simple entre elles: avec trois seulement, leur somme se rangeait
             // en un damier regulier de points brillants, tres visible au
             // dezoom. Des periodes incommensurables cassent le reseau.
             g += vec2(0.85, 0.42) * (0.030 * cos(dot(p, vec2(0.85, 0.42)) + t * 2.30));
             g += vec2(-0.53, 1.07) * (0.024 * cos(dot(p, vec2(-0.53, 1.07)) - t * 1.90));
             g += vec2(1.37, -0.61) * (0.017 * cos(dot(p, vec2(1.37, -0.61)) + t * 2.71));
             g += vec2(-1.58, -0.93) * (0.012 * cos(dot(p, vec2(-1.58, -0.93)) - t * 2.07));
             g += vec2(1.91, -1.37) * (0.009 * cos(dot(p, vec2(1.91, -1.37)) + t * 3.10));
             return g;
           }`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
           // Couleur DEDUITE DE LA PROFONDEUR: c'est elle qui fait lire les
           // baies, les bancs de sable et le large. Une marbrure tres lente
           // empeche le large d'etre d'un seul bleu.
           vec3 eau = mix(uRive, uMoyen, smoothstep(0.0, 2.8, vProf));
           eau = mix(eau, uLarge, smoothstep(2.8, 11.0, vProf));
           // Somme et non produit: le produit de deux sinus a ses lignes nodales
           // sur les axes du monde, qui sont justement les diagonales de l'ecran
           // en vue a 45 degres. La mer se retrouvait rayee de droites.
           float marbre = 0.6 * sin(dot(vMonde.xz, vec2(0.021, 0.013)) + uTemps * 0.07)
                        + 0.4 * sin(dot(vMonde.xz, vec2(-0.009, 0.017)) - uTemps * 0.05);
           // Les cretes de houle sont plus claires que les creux: c'est ce qui
           // donne son relief a la mer en vue orthographique.
           diffuseColor.rgb = eau * (0.95 + 0.08 * marbre + 0.22 * vMonde.y);
           // Sur les hauts-fonds l'eau laisse voir le sable; passe deux metres
           // elle est presque opaque. Le seuil est bas expres: le bord du monde
           // est a 1,80 m au moins, l'eau y couvre donc deja le plateau, et le
           // sable clair du large ne remonte pas laver la mer.
           diffuseColor.a = mix(0.44, 0.97, smoothstep(0.1, 2.2, vProf));`,
        )
        .replace(
          '#include <normal_fragment_begin>',
          `#include <normal_fragment_begin>
           vec2 pente = penteEau(vMonde.xz, uTemps);
           // La pente est en monde, la normale en vue: viewMatrix fait le pont.
           normal = normalize(normal - (viewMatrix * vec4(pente.x, 0.0, pente.y, 0.0)).xyz * 1.7);`,
        )
        .replace(
          '#include <envmap_fragment>',
          `#include <envmap_fragment>
           // Reflet du soleil. La camera est orthographique: sa direction est la
           // meme pour tous les pixels et se lit dans viewMatrix.
           vec3 versCamera = normalize(vec3(viewMatrix[0][2], viewMatrix[1][2], viewMatrix[2][2]));
           vec3 nMonde = normalize(vec3(-pente.x, 1.0, -pente.y));
           vec3 demi = normalize(uSoleil + versCamera);
           // On mesure l'ECART a l'eau plate (dont le produit scalaire vaut
           // demi.y) plutot qu'un Blinn-Phong classique: avec une houle qui ne
           // penche que de quelques degres, un exposant assez grand pour serrer
           // le reflet l'aurait eteint partout. L'ecart est ensuite ramene a la
           // part horizontale de la bissectrice, sinon un soleil rasant, qui
           // l'ecrase, allumait la mer entiere au lieu des seules cretes.
           float ecart = (dot(nMonde, demi) - demi.y) / max(length(demi.xz), 0.05);
           float pointe = smoothstep(0.055, 0.110, ecart);
           float voile = smoothstep(0.0, 0.120, ecart);
           // Le scintillement va par PLAQUES: sans cette modulation lente, la
           // mer brillait d'un bout a l'autre de l'ecran avec la meme densite,
           // ce qui trahissait la formule.
           float plaques = 0.45 + 0.55 * smoothstep(-0.5, 0.6,
             0.6 * sin(dot(vMonde.xz, vec2(0.043, 0.026)) + uTemps * 0.11)
           + 0.4 * sin(dot(vMonde.xz, vec2(-0.019, 0.031)) - uTemps * 0.09));
           // Rien sur les hauts-fonds: le sable n'a pas a scintiller.
           outgoingLight += uEclat * (pointe * 0.95 * plaques + voile * 0.16) * smoothstep(0.5, 2.5, vProf);`,
        )
    }
    materiau.needsUpdate = true
  }

  /**
   * Ressac: le ruban d'ecume avance et recule le long de la normale a la cote.
   *
   * Deux sinus dephases par la position font COURIR le mouvement le long du
   * rivage, au lieu de le faire respirer partout en meme temps.
   */
  private installerNuanceurEcume(materiau: THREE.Material): void {
    materiau.customProgramCacheKey = (): string => 'eau-ecume'
    materiau.onBeforeCompile = (shader) => {
      shader.uniforms['uTemps'] = this.uTemps
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTemps;
           attribute vec2 aMer;
           attribute float aPhase;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           float ressac = 0.55 * sin(uTemps * 1.15 + aPhase) + 0.20 * sin(uTemps * 0.61 - aPhase * 1.7);
           transformed.xz += aMer * ressac;`,
        )
      // Le ruban est a DOUBLE FACE et sa couture n'a pas d'orientation: les
      // segments de marching squares apparient leurs deux bouts dans l'ordre ou
      // ils tombent, un quad sur deux se retrouve donc a l'envers. Avec un
      // materiau eclaire, three.js retourne alors sa normale, le soleil passe
      // sous la face et une plaque d'ecume sur deux virait au vert-de-gris. On
      // impose la verticale: de l'ecume, ca ne regarde jamais que le ciel.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <normal_fragment_begin>',
        `#include <normal_fragment_begin>
         normal = normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz);`,
      )
    }
    materiau.needsUpdate = true
  }

  // --------------------------------------------------------------------- maj

  maj(tempsSec: number): void {
    // Houle, rides et ressac sont calcules par le GPU: les faire en JS demandait
    // le reteleversement de deux attributs a chaque image pour le meme resultat.
    this.uTemps.value = tempsSec
    for (const c of this.chutes) c.maj(tempsSec)

    if (this.matEcume) {
      // Le ruban entier respire, par-dessus le ressac de chaque segment. La
      // nuit ne le retire qu'un peu: son materiau est eclaire, il s'eteint donc
      // deja de lui-meme.
      this.matEcume.opacity = (0.86 + 0.14 * Math.sin(tempsSec * 0.7)) * (1 - this.nuit * 0.2)
    }
    this.majReflet()
  }

  /**
   * Le reflet suit la SEULE lumiere directionnelle de la scene (soleil le jour,
   * lune la nuit), retrouvee par son nom.
   *
   * On la lit au lieu de se la faire passer en parametre pour ne pas toucher a
   * l'appelant, et surtout pour ne pas etre tente d'en ajouter une: le nombre de
   * lumieres de la scene fait partie de la cle de cache des programmes three.js,
   * une lumiere de plus recompilerait tous les nuanceurs du jeu.
   */
  private majReflet(): void {
    if (!this.soleil) {
      const trouve = this.scene.getObjectByName('soleil')
      if (trouve && (trouve as THREE.DirectionalLight).isDirectionalLight) {
        this.soleil = trouve as THREE.DirectionalLight
      }
      this.cibleSoleil = this.scene.getObjectByName('soleil-cible') ?? null
    }
    const soleil = this.soleil
    if (!soleil) return
    this.uSoleil.value.copy(soleil.position)
    if (this.cibleSoleil) this.uSoleil.value.sub(this.cibleSoleil.position)
    if (this.uSoleil.value.lengthSq() < 1e-6) this.uSoleil.value.set(0, 1, 0)
    this.uSoleil.value.normalize()
    // L'intensite de la source monte a 2.05 a midi et tombe a 0.18 sous la lune.
    // On retranche le seuil de la lune avant de mettre a l'echelle: le reflet
    // est franc en plein jour, ambre et discret au couchant, et la nuit il ne
    // reste qu'un fil d'argent au lieu d'un voile gris sur toute la mer.
    this.uEclat.value.copy(soleil.color).multiplyScalar(clamp((soleil.intensity - 0.15) * 0.30, 0, 0.62))
  }

  /** 0 = plein jour, 1 = pleine nuit. */
  couleurEau(nuit: number): void {
    this.nuit = clamp(nuit, 0, 1)
    this.uRive.value.setHex(JOUR_RIVE).lerp(TEINTE_NUIT_RIVE, this.nuit)
    this.uMoyen.value.setHex(JOUR_MOYEN).lerp(TEINTE_NUIT_MOYEN, this.nuit)
    this.uLarge.value.setHex(JOUR_LARGE).lerp(TEINTE_NUIT_LARGE, this.nuit)
    for (const c of this.chutes) c.couleurEau(this.nuit)
  }

  dispose(): void {
    for (const c of this.chutes) c.dispose()
    this.scene.remove(this.maillage)
    if (this.ecume) {
      this.maillage.remove(this.ecume)
      this.ecume.geometry.dispose()
      this.ecume = null
    }
    if (this.fond) {
      this.scene.remove(this.fond)
      this.fond.geometry.dispose()
      this.fond = null
    }
    this.matFond?.dispose()
    this.matFond = null
    this.matEcume?.dispose()
    this.matEcume = null
    this.geo.dispose()
    this.mat.dispose()
    this.soleil = null
    this.cibleSoleil = null
  }
}
